import { i18n } from "../translate/i18n";

// Mensagens de sistema que o backend grava em português no body da mensagem
// (UpdateTicketService: encerramento, transferência, início do chamado;
// HandleRatingReplyService: nota dada pelo cliente).
// São reconhecidas pelo padrão e exibidas no idioma da interface; qualquer
// outro texto passa direto.

const DATE_AT = /^(\d{2}\/\d{2}\/\d{4}) às (\d{2}:\d{2}(?::\d{2})?)$/;

const clean = (value) => String(value || "").trim();

const localizeDate = (text) => {
  const m = clean(text).match(DATE_AT);
  return m ? i18n.t("common.dateTimeAt", { date: m[1], time: m[2] }) : clean(text);
};

const localizeUser = (name) =>
  clean(name).toLowerCase() === "sistema" ? i18n.t("messagesList.system.systemUser") : clean(name);

const localizeQueue = (name) =>
  clean(name) === "ADMINISTRATIVO" ? i18n.t("messagesList.system.adminQueue") : clean(name);

const localizeFrom = (name) =>
  clean(name) === "Fila" ? i18n.t("messagesList.system.queueFallback") : clean(name);

const RATING_HIDDEN_RE = /^Cliente avaliou o atendimento em (.+) \(Ticket #(\d+)\)$/;
const RATING_LEGACY_RE = /^Cliente avaliou o atendimento com nota (\d+) de (\d+) em (.+) \(Ticket #(\d+)\)$/;

const PATTERNS = [
  {
    re: /^Atendimento finalizado por (.+) em (.+) \(Ticket #(\d+)\)$/,
    key: "closedBy",
    params: (m) => ({ user: localizeUser(m[1]), date: localizeDate(m[2]), ticket: m[3] }),
  },
  {
    re: /^Chamado assumido por \((.+)\) - (.+)$/,
    key: "takenBy",
    params: (m) => ({ user: m[1], date: localizeDate(m[2]) }),
  },
  // Chamado em Atendendo transferido sem usuário (volta para Aguardando).
  // Antes do padrão genérico abaixo, que também casaria.
  {
    re: /^Chamado transferido por \((.+)\) - De (.+) para Aguardando na fila (.+) - (.+)$/,
    key: "transferredToPendingQueue",
    params: (m) => ({ user: m[1], from: localizeFrom(m[2]), queue: m[3], date: localizeDate(m[4]) }),
  },
  {
    re: /^Chamado transferido por \((.+)\) - De (.+) para Aguardando sem fila - (.+)$/,
    key: "transferredToPendingNoQueue",
    params: (m) => ({ user: m[1], from: localizeFrom(m[2]), date: localizeDate(m[3]) }),
  },
  {
    re: /^Chamado transferido por \((.+)\) - De (.+) para (.+) - (.+)$/,
    key: "transferredBy",
    params: (m) => ({ user: m[1], from: localizeFrom(m[2]), to: m[3], date: localizeDate(m[4]) }),
  },
  {
    re: /^Chamado transferido de (.+) para (.+) - (.+)$/,
    key: "transferredFromTo",
    params: (m) => ({ from: m[1], to: m[2], date: localizeDate(m[3]) }),
  },
  // Aviso de avaliação: o texto genérico NUNCA mostra a nota (o atendente não
  // pode saber). Quem tem acesso às Avaliações vê a nota pelo
  // components/RatingSystemText, que usa ratingSystemText abaixo.
  {
    re: RATING_HIDDEN_RE,
    key: "ratedHidden",
    params: (m) => ({ date: localizeDate(m[1]), ticket: m[2] }),
  },
  {
    // Formato antigo (07/10/2026), gravado com a nota no texto.
    re: RATING_LEGACY_RE,
    key: "ratedHidden",
    params: (m) => ({ date: localizeDate(m[3]), ticket: m[4] }),
  },
  {
    re: /^Início do chamado - (.+) \((.+)\) - (.+)$/,
    key: "started",
    params: (m) => ({ queue: localizeQueue(m[1]), user: m[2], date: localizeDate(m[3]) }),
  },
];

export const translateSystemMessage = (body) => {
  const text = String(body || "").replace(/\s+/g, " ").trim();
  if (!text) return body;
  for (const { re, key, params } of PATTERNS) {
    const m = text.match(re);
    if (m) return i18n.t(`messagesList.system.${key}`, params(m));
  }
  return body;
};

// Aviso de avaliação com a nota, para quem tem acesso às Avaliações. Sem
// `rating` (sem acesso ou nota ainda carregando) cai no texto sem nota.
export const ratingSystemText = (body, rating) => {
  const text = String(body || "").replace(/\s+/g, " ").trim();
  const hidden = text.match(RATING_HIDDEN_RE);
  const legacy = text.match(RATING_LEGACY_RE);
  if (!rating || (!hidden && !legacy)) return translateSystemMessage(body);
  const [date, ticket] = hidden ? [hidden[1], hidden[2]] : [legacy[3], legacy[4]];
  return i18n.t("messagesList.system.rated", {
    rate: rating.rate,
    max: rating.max,
    date: localizeDate(date),
    ticket,
  });
};

export default translateSystemMessage;
